<template>
  <div class="qa-board-list">
    <a-empty
      v-if="!groups.length"
      description="Нет планировок" />

    <a-collapse v-else>
      <a-collapse-panel
        v-for="group in groups"
        :key="group.key"
        :collapsible="group.items.length ? undefined : 'disabled'">
        <template #header>
          <span>
            <span v-if="group.hasUrgent">🔥 </span>
            {{ group.name }}
            <a-tag style="margin-left: 8px;">{{ group.items.length }}</a-tag>
          </span>
        </template>

        <a-table
          :data-source="group.items"
          :pagination="false"
          :row-key="rowKey"
          size="small">
          <a-table-column
            key="type"
            title="Тип"
            width="72px">
            <template #default="{ record }">
              <a-tag :color="record.type === PlanType.Type3d ? 'purple' : 'cyan'">
                {{ record.type === PlanType.Type3d ? '3D' : '2D' }}
              </a-tag>
            </template>
          </a-table-column>

          <a-table-column
            key="name"
            title="Планировка"
            data-index="name">
            <template #default="{ record }">
              <router-link :to="linkTo(record)">
                {{ record.name }}
              </router-link>
            </template>
          </a-table-column>

          <a-table-column
            v-if="showBoardStatus"
            key="board_status"
            title="Статус">
            <template #default="{ record }">
              <a-tag :color="boardStatusColor(record.board_status)">
                {{ boardStatusLabel(record.board_status) }}
              </a-tag>
            </template>
          </a-table-column>

          <a-table-column
            key="deadline"
            title="Дедлайн"
            data-index="deadline_at">
            <template #default="{ record }">
              <a-tag :color="deadlineColor(record.deadline_at)">
                {{ deadlineLabel(record.deadline_at) }}
              </a-tag>
            </template>
          </a-table-column>

          <a-table-column
            key="person"
            :title="personTitle">
            <template #default="{ record }">
              {{ personName(record) }}
            </template>
          </a-table-column>

          <a-table-column
            key="iteration"
            title="Итерация"
            data-index="iteration">
            <template #default="{ record }">
              {{ record.iteration }}-я
            </template>
          </a-table-column>

          <a-table-column
            v-if="showRenderStatus"
            key="render"
            title="Рендер">
            <template #default="{ record }">
              <a-tag
                v-if="record.type === PlanType.Type3d"
                :color="renderStatusColor(record.render_status)">
                {{ renderStatusLabel(record.render_status) }}
              </a-tag>
              <span v-else>—</span>
            </template>
          </a-table-column>

          <a-table-column
            key="issues"
            :title="recommendationsMode ? 'Рекомендации' : 'Замечаний'">
            <template #default="{ record }">
              <div
                v-if="recommendationsMode"
                class="qa-board-list__recommendations">
                <div
                  v-for="recommendation in record.recommendations ?? []"
                  :key="recommendation.id"
                  class="qa-board-list__recommendation"
                  @click.stop>
                  <span class="qa-board-list__recommendation-title">
                    {{ recommendation.title }}
                  </span>
                  <a-popconfirm
                    title="Удалить рекомендацию?"
                    ok-text="Удалить"
                    cancel-text="Отмена"
                    ok-type="danger"
                    @confirm="$emit('delete-recommendation', recommendation.id)">
                    <a-button
                      type="text"
                      size="small"
                      danger>
                      <close-outlined />
                    </a-button>
                  </a-popconfirm>
                </div>
              </div>
              <template v-else>
                <div v-if="record.issues.total">
                  Исправлено {{ record.issues.resolved }} из {{ record.issues.total }}
                </div>
                <span v-else>—</span>
                <div
                  v-if="(record.issue_stages ?? []).length"
                  class="qa-board-list__stages">
                  <a-tag
                    v-for="stage in record.issue_stages"
                    :key="stage"
                    color="volcano">
                    {{ stageLabel(stage) }}
                  </a-tag>
                </div>
              </template>
            </template>
          </a-table-column>
        </a-table>
      </a-collapse-panel>
    </a-collapse>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import { PlanType } from '@/enums/planType';

const props = defineProps({
  items: { type: Array, default: () => [] },
  itemRouteName: { type: String, required: true },
  extraQuery: { type: Object, default: () => ({}) },
  personColumn: { type: String, default: 'ninja' },
  search: { type: String, default: '' },
  recommendationsMode: { type: Boolean, default: false },
  showRenderStatus: { type: Boolean, default: false },
  showBoardStatus: { type: Boolean, default: false },
  // Порядок статусов в сортировке: у проверяющего и у ниндзя приоритеты разные.
  statusOrder: { type: Array, default: () => ['awaiting', 'rejected', 'in_progress'] },
  // Куда вести, когда исправлять нечего (ниндзя — на страницу CRM-планировки): { '2d', '3d' }.
  crmPlanRoutes: { type: Object, default: null },
  // Апартаменты, назначенные пользователю. Показываются группой даже без планировок.
  apartments: { type: Array, default: () => [] },
});

defineEmits(['delete-recommendation']);

const URGENT_HOURS = 4;

const personTitle = computed(() => (props.personColumn === 'reviewer' ? 'Проверяющий' : 'Ниндзя'));
const personName = (record) => record[props.personColumn]?.name ?? '—';
const rowKey = (record) => `${record.type}-${record.crm_plan_id}`;
const renderStatusLabel = (status) => {
  switch (status) {
    case 1:
      return 'В процессе';
    case 2:
      return 'Выполнено';
    case -1:
      return 'Ошибка';
    default:
      return 'Не выполнялось';
  }
};
const renderStatusColor = (status) => {
  switch (status) {
    case 1:
      return 'blue';
    case 2:
      return 'green';
    case -1:
      return 'red';
    default:
      return 'default';
  }
};

const boardStatusLabel = (status) => ({
  awaiting: 'Ждёт проверки',
  rejected: 'Отклонено',
  in_progress: 'В работе',
}[status] ?? 'В работе');

const boardStatusColor = (status) => ({
  awaiting: 'gold',
  rejected: 'red',
  in_progress: 'blue',
}[status] ?? 'blue');

const STAGE_LABELS = {
  jpg: 'JPG', svg: 'SVG', '2d': '2D', '3d': '3D', tour: '360',
};
const stageLabel = (stage) => STAGE_LABELS[stage] ?? stage;

const statusRank = (status) => {
  const index = props.statusOrder.indexOf(status);
  return index === -1 ? props.statusOrder.length : index;
};

const hoursLeft = (deadline) => {
  if (!deadline) return null;
  return (new Date(deadline).getTime() - Date.now()) / 3_600_000;
};

const deadlineColor = (deadline) => {
  const left = hoursLeft(deadline);
  if (left === null) return 'default';
  if (left <= URGENT_HOURS) return 'red';
  if (left <= 24) return 'orange';
  return 'green';
};

const deadlineLabel = (deadline) => {
  const left = hoursLeft(deadline);
  if (left === null) return 'без срока';
  if (left < 0) return 'просрочено';
  if (left < 24) return `ост. ${Math.round(left)}ч`;
  return `ост. ${Math.round(left / 24)}д`;
};

const linkTo = (record) => {
  // В исправление ведём, только когда реально есть что чинить: планировка отклонена
  // (или уже прошла итерацию) И по ней есть замечания. Иначе (в т.ч. когда админ
  // нарочно перевёл в «Отклонено» без замечаний) — на страницу CRM-планировки.
  const routes = props.crmPlanRoutes;
  const hasFixWork = (record.issues?.total ?? 0) > 0
    && (record.board_status === 'rejected' || (record.iteration ?? 1) > 1);

  if (routes && !hasFixWork) {
    return {
      name: record.type === PlanType.Type3d ? routes['3d'] : routes['2d'],
      params: { id: record.crm_plan_id },
    };
  }

  return {
    name: props.itemRouteName,
    params: { id: record.crm_plan_id },
    query: { type: record.type, ...props.extraQuery },
  };
};

const groups = computed(() => {
  const byApartment = new Map();

  const ensureGroup = (key, name) => {
    if (!byApartment.has(key)) {
      byApartment.set(key, {
        key: String(key),
        name,
        items: [],
        hasUrgent: false,
        rank: Number.MAX_SAFE_INTEGER,
      });
    }
    return byApartment.get(key);
  };

  props.apartments.forEach((apartment) => ensureGroup(apartment.id, apartment.name));

  props.items.forEach((item) => {
    const group = ensureGroup(
      item.apartment?.id ?? 'none',
      item.apartment?.name ?? 'Без апартамента',
    );
    group.items.push(item);
    const left = hoursLeft(item.deadline_at);
    if (left !== null && left <= URGENT_HOURS) group.hasUrgent = true;
    group.rank = Math.min(group.rank, statusRank(item.board_status));
  });

  // Сначала по приоритету статуса, внутри статуса — по срочности дедлайна.
  const sortRows = (a, b) => {
    const rankDiff = statusRank(a.board_status) - statusRank(b.board_status);
    if (rankDiff !== 0) return rankDiff;

    const la = hoursLeft(a.deadline_at);
    const lb = hoursLeft(b.deadline_at);
    if (la === null) return 1;
    if (lb === null) return -1;
    return la - lb;
  };

  const result = [...byApartment.values()]
    .map((group) => ({ ...group, items: [...group.items].sort(sortRows) }))
    .sort((a, b) => {
      if (a.hasUrgent !== b.hasUrgent) return a.hasUrgent ? -1 : 1;
      if (a.rank !== b.rank) return a.rank - b.rank;
      if (!a.items.length !== !b.items.length) return a.items.length ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

  const term = props.search.trim().toLowerCase();
  return term
    ? result.filter((group) => group.name.toLowerCase().includes(term))
    : result;
});
</script>

<style scoped>
.qa-board-list__recommendations {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 220px;
}
.qa-board-list__recommendation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 6px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  background: #fafafa;
}
.qa-board-list__stages {
  margin-top: 4px;
}
.qa-board-list__recommendation-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
